import {
  type AppearanceMode,
  useAppearanceMode,
} from '@/app-shell/app-root/useAppearanceMode';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { MonitorIcon, MoonIcon, SunIcon } from 'lucide-react';
import { useIntl } from 'react-intl';
import { appearanceModeMessages } from './AppearanceModeMenuItems.messages';

const APPEARANCE_OPTIONS = [
  {
    value: 'light' as AppearanceMode,
    tooltip: appearanceModeMessages.light,
    icon: <SunIcon />,
  },
  {
    value: 'dark' as AppearanceMode,
    tooltip: appearanceModeMessages.dark,
    icon: <MoonIcon />,
  },
  {
    value: 'system' as AppearanceMode,
    tooltip: appearanceModeMessages.system,
    icon: <MonitorIcon />,
  },
];

/**
 * Segmented control for appearance mode, rendered inside the
 * UserAccountMenu header area (between user info and menu items).
 */
export function AppearanceModeMenuItems() {
  const { mode, setMode } = useAppearanceMode();
  const intl = useIntl();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();

  return (
    <div className="px-2 pb-1.5 pt-1">
      <SegmentedControl
        value={mode}
        onValueChange={(value) => {
          if (value === 'light' || value === 'dark' || value === 'system') {
            if (isOfflineGated) {
              setMode(value, { suppressNetworkSideEffects: true });
            } else {
              setMode(value);
            }
          }
        }}
      >
        {/* Pin to 32/24 -- DS default of 36/28 is too tall for this dense menu surface. */}
        <SegmentedControlList className="!h-8 w-full">
          {APPEARANCE_OPTIONS.map(({ value, tooltip, icon }) => {
            const label = intl.formatMessage(tooltip);

            return (
              <Tooltip key={value} delayDuration={300}>
                <TooltipTrigger asChild>
                  <span className="flex flex-1">
                    <SegmentedControlTrigger
                      value={value}
                      iconOnly
                      aria-label={label}
                      className="!h-6 w-full"
                      data-testid={`appearance-mode-${value}`}
                    >
                      {icon}
                    </SegmentedControlTrigger>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">{label}</TooltipContent>
              </Tooltip>
            );
          })}
        </SegmentedControlList>
      </SegmentedControl>
    </div>
  );
}
